'use client';
/**
 * MON AUTO-ÉVALUATION DE PAILLASSE.
 *
 * Demande de la RQ le 14/08/2026 : « prévoir également des auto-évaluations
 * que le technicien peut faire pour sa paillasse ».
 *
 * ── L'avertissement en tête n'est pas décoratif ─────────────────────────────
 *
 * Il est le premier élément de l'écran, avant les grilles, et il ne se replie
 * pas. Une grille de contrôle remplie, datée, signée et rangée dans le
 * logiciel qualité RESSEMBLE à une preuve d'audit interne — au point qu'elle
 * finirait produite comme telle devant un évaluateur, de bonne foi. La §8.8
 * exige l'indépendance de l'auditeur ; ici la personne évalue son propre
 * poste. Le dire une fois, à l'endroit où le geste se fait, coûte trois lignes
 * et évite une non-conformité d'accréditation.
 *
 * ── Pourquoi trois réponses et pas deux ────────────────────────────────────
 *
 * « Non applicable » est une réponse à part entière : la grille commune parle
 * d'enceintes et de contrôles internes, et tous les postes n'en ont pas.
 * Forcer un « oui » faute de mieux fabriquerait une conformité, forcer un
 * « non » fabriquerait un problème. La troisième case est ce qui rend les deux
 * autres honnêtes.
 *
 * ── Le « si non » s'ouvre AU MOMENT du non ──────────────────────────────────
 *
 * La consigne (`siNon`) n'apparaît pas d'avance : la lire avant de répondre
 * oriente la réponse. Elle apparaît quand elle sert, c'est-à-dire quand il y a
 * quelque chose à faire — et elle dit un GESTE, jamais un reproche.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { I, EmptyState, Skeleton, card } from '@/components/ui';
import { Bouton } from '@/shared/Bouton';
import { EnteteEcran } from '@/components/EnteteEcran';
import { fmtDate } from '@/lib/meta';

type Point = { id: string; label: string; siNon: string; article?: string };
type Grille = { id: string; nom: string; postes: string[]; items: Point[] };
type Faite = {
  ref: string; grilleId: string; grilleNom: string; poste?: string | null;
  auteur: string; auteurNom?: string; dateAuto: string;
  reponses?: Record<string, string>; nbNon?: number; remarques?: string | null;
};
type Contexte = {
  grilles: Grille[]; mesPostes: string[]; lectureLarge: boolean;
  auto: Faite[]; moi: { key: string; nom: string };
};

const REPONSES = [
  { v: 'oui', label: 'Oui', couleur: 'var(--green-600)' },
  { v: 'non', label: 'Non', couleur: 'var(--red-600)' },
  { v: 'na', label: 'Non applicable', couleur: 'var(--text-muted)' },
] as const;

/** Encadré de section. L'application n'expose pas de composant « carte »
 *  partagé : les écrans dessinent leur propre encadré, et en introduire un
 *  ici, utilisé par un seul écran, serait un composant partagé qui ne partage
 *  rien. */
function Bloc({ titre, actions, children }: { titre: string; actions?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden', background: 'var(--surface-card)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 14px', background: 'var(--gray-25)', borderBottom: '1px solid var(--border-default)' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>{titre}</span>
        {actions}
      </div>
      <div style={{ padding: '12px 14px' }}>{children}</div>
    </div>
  );
}

export function AutoEvaluation() {
  const [ctx, setCtx] = useState<Contexte | null>(null);
  const [erreur, setErreur] = useState('');
  const [grilleId, setGrilleId] = useState('');
  const [reponses, setReponses] = useState<Record<string, string>>({});
  const [poste, setPoste] = useState('');
  const [remarques, setRemarques] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [confirmee, setConfirmee] = useState<{ ref: string; nbNon: number } | null>(null);

  const charger = useCallback(async () => {
    try {
      const r = await fetch('/api/ged/ged/auto-evaluations', { credentials: 'include' });
      if (!r.ok) { setErreur('Les auto-évaluations n’ont pas pu être chargées.'); return; }
      setCtx(await r.json());
    } catch { setErreur('Les auto-évaluations n’ont pas pu être chargées.'); }
  }, []);

  /* ⚠️ FAUX POSITIF ASSUMÉ de `react-hooks/set-state-in-effect` (02/09/2026).
     `charger` est un `useCallback` ASYNC : son `setState` se produit APRÈS un
     `await`, donc dans une continuation, jamais dans le corps de l'effet. La
     règle ne franchit pas la frontière asynchrone et l'annonce quand même.
     C'est l'usage canonique d'un effet — « s'abonner à un système externe ».
     ⚠️ Le silence est CIBLÉ, ligne à ligne : désactiver la règle pour le
     fichier masquerait un vrai cas ajouté demain. */
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { void charger(); }, [charger]);

  /** Les grilles de mes postes d'abord, la commune ensuite, le reste après.
   *  Aucune n'est masquée — cf. l'en-tête du contrôleur. */
  const grilles = useMemo(() => {
    if (!ctx) return [];
    const miens = new Set(ctx.mesPostes || []);
    const rang = (g: Grille) => (g.postes?.some((p) => miens.has(p)) ? 0 : g.postes?.length ? 2 : 1);
    return [...ctx.grilles].sort((a, b) => rang(a) - rang(b));
  }, [ctx]);

  const grille = grilles.find((g) => g.id === grilleId) || null;
  const restants = grille ? grille.items.filter((it) => !reponses[it.id]).length : 0;
  const nbNon = grille ? grille.items.filter((it) => reponses[it.id] === 'non').length : 0;

  const enregistrer = async () => {
    if (!grille || restants) return;
    setEnvoi(true); setErreur('');
    try {
      const r = await fetchEcriture('/api/ged/ged/auto-evaluations', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ grilleId: grille.id, reponses, poste, remarques }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setErreur(j?.error?.message || j?.error || 'Enregistrement refusé.'); return; }
      setConfirmee({ ref: j.ref, nbNon: j.nbNon });
      setGrilleId(''); setReponses({}); setRemarques('');
      await charger();
    } finally { setEnvoi(false); }
  };


  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {/* ⚠️ CET ÉCRAN N'AVAIT AUCUN EN-TÊTE (relevé SYS-1 du 26/08/2026) — le
          seul des cinquante-cinq. On y arrivait sur un bandeau ambre sans savoir
          où l'on était : l'avertissement ouvrait la page, ce qui le faisait lire
          comme une alerte SURVENUE plutôt que comme le cadrage de la rubrique.
          Le titre situe ; l'avertissement, juste dessous, garde toute sa force
          et n'a plus à porter l'identité de l'écran. */}
      <EnteteEcran rubrique="Évaluation" clauseISO="8.8" titre="Auto-évaluation" />
      {/* ── L'avertissement, en premier et sans repli ────────────────────── */}
      <div style={{
        display: 'flex', gap: 10, alignItems: 'flex-start',
        background: 'var(--amber-50)', border: '1px solid var(--amber-200)',
        borderRadius: 'var(--radius-md)', padding: '11px 14px',
      }}>
        <I n="alert-triangle" s={16} style={{ color: 'var(--amber-700)', flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--amber-800)' }}>
          <strong>Une auto-évaluation n’est pas un audit interne.</strong> Vous évaluez votre
          propre poste : la norme (§8.8) demande, pour un audit interne, un auditeur indépendant
          du secteur audité. Ce que vous remplissez ici ne compte donc pas dans le programme
          d’audit et ne produit pas d’écart — c’est un outil pour <em>voir venir</em>, pas pour
          se juger. Un « non » n’est pas une faute : c’est exactement ce qu’on cherche.
        </div>
      </div>

      {confirmee && (
        <div style={{
          display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between',
          background: 'var(--green-50)', border: '1px solid var(--green-200)',
          borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: 12.5, color: 'var(--green-800)',
        }}>
          <span>
            <strong>Auto-évaluation {confirmee.ref} enregistrée.</strong>{' '}
            {confirmee.nbNon === 0
              ? 'Aucun point en écart aujourd’hui.'
              : `${confirmee.nbNon} point(s) à reprendre — les gestes sont rappelés ci-dessous dans l’historique.`}
          </span>
          <button type="button" onClick={() => setConfirmee(null)}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', padding: 4 }}>
            <I n="x" s={13} />
          </button>
        </div>
      )}

      {erreur && (
        <div style={{ background: 'var(--red-50)', border: '1px solid var(--red-200)', borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: 12.5, color: 'var(--red-700)' }}>
          {erreur}
          {!ctx && (
            <div style={{ marginTop: 8 }}>
              <Bouton taille="sm" type="button" onClick={() => { setErreur(''); void charger(); }}>Réessayer</Bouton>
            </div>
          )}
        </div>
      )}

      {!ctx && !erreur && <div style={card}><Skeleton rows={4} /></div>}

      {ctx && !grille && (
        <Bloc titre="Choisir une grille">
          {grilles.length === 0 && (
            <EmptyState titre="Aucune grille d’auto-évaluation n’est définie."
              indice="Les grilles sont installées avec le fonds ISO ; le RQ peut les ajuster dans le paramétrage." />
          )}
          <div style={{ display: 'grid', gap: 8 }}>
            {grilles.map((g) => {
              const pourMoi = g.postes?.some((p) => (ctx.mesPostes || []).includes(p));
              return (
                <button key={g.id} type="button" onClick={() => { setGrilleId(g.id); setReponses({}); setConfirmee(null); }}
                  style={{
                    textAlign: 'start', background: 'var(--surface-card)', cursor: 'pointer',
                    border: `1px solid ${pourMoi ? 'var(--color-primary-active)' : 'var(--border-default)'}`,
                    borderRadius: 'var(--radius-md)', padding: '11px 13px',
                    fontFamily: 'var(--font-sans)',
                  }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-strong)' }}>{g.nom}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>
                    {g.items.length} point(s)
                    {g.postes?.length ? ` · postes : ${g.postes.join(', ')}` : ' · tous postes'}
                    {pourMoi ? ' · correspond à votre poste' : ''}
                  </div>
                </button>
              );
            })}
          </div>
        </Bloc>
      )}

      {ctx && grille && (
        <Bloc titre={grille.nom}
          actions={
            <button type="button" onClick={() => { setGrilleId(''); setReponses({}); }}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 12.5, color: 'var(--text-muted)' }}>
              Changer de grille
            </button>
          }>
          <div style={{ display: 'grid', gap: 10 }}>
            <label style={{ display: 'grid', gap: 4, maxWidth: 340 }}>
              <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
                Poste évalué (facultatif)
              </span>
              <select value={poste} onChange={(e) => setPoste(e.target.value)}
                style={{ fontFamily: 'var(--font-sans)', fontSize: 13, padding: '7px 9px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)' }}>
                <option value="">—</option>
                {/* Les postes proposés sont CEUX DE LA PERSONNE, servis par le
                    moteur. Dérouler les quarante postes du laboratoire pour
                    qu'elle en choisisse un des siens serait la faire chercher. */}
                {(ctx.mesPostes || []).map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>

            {grille.items.map((it, i) => {
              const rep = reponses[it.id];
              return (
                <div key={it.id} style={{ borderTop: i ? '1px solid var(--border-subtle)' : 'none', paddingTop: i ? 10 : 0, display: 'grid', gap: 7 }}>
                  <div style={{ fontSize: 13.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
                    {it.article && (
                      <span title="Article de l’ISO 15189:2022 auquel ce point se rattache."
                        style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, fontWeight: 700, color: 'var(--color-primary-active)', marginInlineEnd: 8, cursor: 'help' }}>
                        §{it.article}
                      </span>
                    )}
                    {it.label}
                  </div>
                  <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                    {REPONSES.map((r) => {
                      const on = rep === r.v;
                      return (
                        <button key={r.v} type="button"
                          onClick={() => setReponses((p) => ({ ...p, [it.id]: r.v }))}
                          aria-pressed={on}
                          style={{
                            fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: on ? 700 : 500,
                            padding: '5px 12px', cursor: 'pointer', borderRadius: 'var(--radius-pill)',
                            border: `1px solid ${on ? r.couleur : 'var(--border-default)'}`,
                            background: on ? r.couleur : 'var(--surface-card)',
                            color: on ? 'var(--surface-card)' : 'var(--text-body)',
                          }}>
                          {r.label}
                        </button>
                      );
                    })}
                  </div>
                  {rep === 'non' && (
                    <div style={{ background: 'var(--gray-25)', borderInlineStart: '3px solid var(--amber-400)', borderRadius: '0 var(--radius-sm) var(--radius-sm) 0', padding: '8px 11px', fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-body)' }}>
                      <strong>À faire :</strong> {it.siNon}
                    </div>
                  )}
                </div>
              );
            })}

            <label style={{ display: 'grid', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
                Remarques (ce que la grille n’a pas prévu)
              </span>
              <textarea value={remarques} onChange={(e) => setRemarques(e.target.value)} rows={3}
                style={{ fontFamily: 'var(--font-sans)', fontSize: 13, padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', resize: 'vertical' }} />
            </label>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <button type="button" onClick={() => void enregistrer()} disabled={!!restants || envoi}
                title={restants ? `${restants} point(s) sans réponse — « non applicable » est une réponse valable.` : ''}
                style={{
                  fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600, padding: '8px 16px',
                  borderRadius: 'var(--radius-md)', border: 'none',
                  background: restants || envoi ? 'var(--gray-200)' : 'var(--color-primary-active)',
                  color: restants || envoi ? 'var(--text-muted)' : 'var(--surface-card)',
                  cursor: restants || envoi ? 'not-allowed' : 'pointer',
                }}>
                {envoi ? 'Enregistrement…' : 'Enregistrer mon auto-évaluation'}
              </button>
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                {restants
                  ? `${restants} point(s) sans réponse — « non applicable » en est une.`
                  : `Tous les points sont renseignés · ${nbNon} « non ».`}
              </span>
            </div>
          </div>
        </Bloc>
      )}

      {ctx && (
        <Bloc titre={ctx.lectureLarge ? 'Auto-évaluations du laboratoire' : 'Mes auto-évaluations passées'}>
          {ctx.auto.length === 0 && (
            <EmptyState titre="Aucune auto-évaluation enregistrée."
              indice="La première se remplit en deux minutes, au poste, en fin de série." />
          )}
          <div style={{ display: 'grid', gap: 7 }}>
            {ctx.auto.map((a) => (
              <div key={a.ref} style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 12.5, borderTop: '1px solid var(--border-subtle)', paddingTop: 7 }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, color: 'var(--color-primary-active)' }}>{a.ref}</span>
                <span style={{ color: 'var(--text-body)' }}>{a.grilleNom}</span>
                <span style={{ color: 'var(--text-muted)' }}>{fmtDate(a.dateAuto)}</span>
                {a.poste && <span style={{ color: 'var(--text-muted)' }}>poste {a.poste}</span>}
                {ctx.lectureLarge && a.auteur !== ctx.moi.key && (
                  <span style={{ color: 'var(--text-muted)' }}>{a.auteurNom || a.auteur}</span>
                )}
                <span style={{ marginInlineStart: 'auto', fontWeight: 600, color: a.nbNon ? 'var(--red-600)' : 'var(--green-600)' }}>
                  {a.nbNon ? `${a.nbNon} point(s) à reprendre` : 'aucun point en écart'}
                </span>
              </div>
            ))}
          </div>
        </Bloc>
      )}
    </div>
  );
}
